import AsyncStorage from '@react-native-async-storage/async-storage';
import { addDoc, collection, doc, getDoc, getDocs, getFirestore, onSnapshot, query, setDoc, where, updateDoc, orderBy, limit, deleteDoc, getCountFromServer, Timestamp } from 'firebase/firestore';
import GeoUtils from '../utility/GeoUtils';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import TimeOps from './TimeOps';


class DataOps {
    static app: any;
    static db: any;
    static storage: any;
    static auth: any;
    static cloudTimestampFields = ['createdAt', 'startAt', 'endAt', 'dateOfBirth', 'updatedAt', 'deletedAt', 'checkInTime', 'checkOutTime'];

    static {
        const firebaseConfig = {
            apiKey: 'AIzaSyCohzJ5cM8UeNU1-5NyaYetkNHj42AfasU',
            authDomain: 'snapcamp-62d3c.firebaseapp.com',
            databaseURL: 'https://snapcamp-62d3c.firebaseio.com',
            projectId: 'snapcamp-62d3c',
            storageBucket: 'snapcamp-62d3c.appspot.com',
            messagingSenderId: '840220596106',
            appId: '1:840220596106:ios:f71d024fae420090311a73',
            measurementId: 'G-measurement-id',
        };
        this.app = initializeApp(firebaseConfig);
        this.db = getFirestore(this.app);
        this.storage = getStorage(this.app);
        this.auth = getAuth(this.app);
    }

    static listenToItemsFromFirebase(collectionName, searchKey, searchValue, callback) {
        try {
            const collectionRef = collection(DataOps.db, collectionName);
            const q = query(collectionRef, where(searchKey, "==", searchValue));

            return onSnapshot(q, (snapshot) => {
                const items = [];
                snapshot.forEach((doc) => {
                    items.push({ id: doc.id, ...doc.data() });
                });
                callback(items);
            });
        } catch (error) {
            console.error('Error listening to items from Firebase:', error);
        }
    }

    static getCloudFunctionBase() {
        return (globalThis as any)?.CLOUD_FN_BASE || 'https://us-central1-snapcamp-62d3c.cloudfunctions.net';
    }

    static normalizeCloudTimestamps<T>(input: T | T[]): T | T[] {
        if (!input || typeof input !== 'object') return input;
        if (Array.isArray(input)) return input.map((item) => this.normalizeCloudTimestamps(item)) as T[];

        const normalized = { ...(input as Record<string, any>) };

        Object.keys(normalized).forEach((key) => {
            const value = normalized[key];

            if (this.cloudTimestampFields.includes(key)) {
                normalized[key] = this.toFirestoreTimestamp(value);
                return;
            }

            if (typeof value === 'object' && value !== null) {
                normalized[key] = this.normalizeCloudTimestamps(value);
            }
        });

        return normalized as T;
    }

    static toFirestoreTimestamp(value: any) {
        if (!value) return value;
        if (value instanceof Timestamp) return value;

        if (typeof value === 'object' && ('seconds' in value || '_seconds' in value)) {
            const seconds = value.seconds ?? value._seconds;
            const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
            return new Timestamp(seconds, nanoseconds);
        }

        if (value instanceof Date) return Timestamp.fromDate(value);
        if (typeof value === 'number') return Timestamp.fromMillis(value);
        return value;
    }

    private static serializeTimestampsForCloud(data: any): any {
        if (!data || typeof data !== 'object') return data;

        const serialized = Array.isArray(data) ? [...data] : { ...data };

        Object.keys(serialized).forEach((key) => {
            const value = serialized[key];
            if (value instanceof Timestamp) {
                serialized[key] = value.toDate().toISOString();
            } else if (value instanceof Date) {
                serialized[key] = value.toISOString();
            } else if (value && typeof value === 'object') {
                serialized[key] = this.serializeTimestampsForCloud(value);
            }
        });

        return serialized;
    }

    private static getMillisFromDisplayDate(dateStr: string): number | null {
        if (!dateStr) return null;
        const parts = dateStr.split('/');
        if (parts.length !== 3) {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return null;
            return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        }

        const [mm, dd, yyyy] = parts;
        const parsed = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10));
        if (isNaN(parsed.getTime())) return null;
        return parsed.getTime();
    }

    static async fetchCloudData<T>(
        endpoint: string,
        params?: Record<string, any>,
        options?: {
            retries?: number;
            timeoutMs?: number;
            suppressErrors?: boolean;
            extractor?: (json: any) => T | T[] | null;
        }
    ): Promise<T | T[] | null> {
        const retries = options?.retries ?? ((globalThis as any)?.CLOUD_FN_RETRY_ATTEMPTS ?? 2);
        const timeoutMs = options?.timeoutMs ?? ((globalThis as any)?.CLOUD_FN_TIMEOUT_MS ?? 5000);
        const suppressErrors = options?.suppressErrors ?? false;
        const extractor = options?.extractor ?? ((json) => json);

        let url = `${this.getCloudFunctionBase()}/${endpoint}`;
        if (params) {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                if (Array.isArray(value)) {
                    value.forEach((item) => queryParams.append(key, String(item)));
                    return;
                }
                queryParams.append(key, String(value));
            });
            const queryString = queryParams.toString();
            if (queryString) url += `?${queryString}`;
        }

        const attemptFetch = async () => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), timeoutMs);

            try {
                const response = await fetch(url, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const json = await response.json();
                const result = extractor(json);
                return this.normalizeCloudTimestamps(result as any) as T | T[] | null;
            } finally {
                clearTimeout(timer);
            }
        };

        let lastError: any = null;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                return await attemptFetch();
            } catch (error) {
                lastError = error;
                if (attempt < retries) {
                    await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
                }
            }
        }

        if (!suppressErrors) {
            console.error(`fetchCloudData failed after retries (${endpoint}):`, lastError);
        }
        return null;
    }

    static async postCloudData<T>(
        endpoint: string,
        body?: Record<string, any>,
        options?: {
            retries?: number;
            timeoutMs?: number;
            suppressErrors?: boolean;
            extractor?: (json: any) => T | T[] | null;
        }
    ): Promise<T | T[] | null> {
        const retries = options?.retries ?? ((globalThis as any)?.CLOUD_FN_RETRY_ATTEMPTS ?? 1);
        const timeoutMs = options?.timeoutMs ?? ((globalThis as any)?.CLOUD_FN_TIMEOUT_MS ?? 5000);
        const suppressErrors = options?.suppressErrors ?? false;
        const extractor = options?.extractor ?? ((json) => json);
        const url = `${this.getCloudFunctionBase()}/${endpoint}`;

        const attemptPost = async () => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), timeoutMs);
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body || {}),
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const json = await response.json();
                const result = extractor(json);
                return this.normalizeCloudTimestamps(result as any) as T | T[] | null;
            } finally {
                clearTimeout(timer);
            }
        };

        let lastError: any = null;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                return await attemptPost();
            } catch (error) {
                lastError = error;
                if (attempt < retries) {
                    await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
                }
            }
        }

        if (!suppressErrors) {
            console.error(`postCloudData failed after retries (${endpoint}):`, lastError);
        }
        return null;
    }

    static async uploadDocumentViaCF(collectionName: string, documentId: string, data: any, merge: boolean = true): Promise<boolean> {
        try {
            if (!documentId) return false;

            const response = await fetch(`${this.getCloudFunctionBase()}/saveDocument`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    collection: collectionName,
                    id: documentId,
                    data,
                    merge,
                }),
            });

            return response.ok;
        } catch (error) {
            console.error('uploadDocumentViaCF error', error);
            return false;
        }
    }

    static async fetchAllDocumentsFromFirebase(collectionName: string) {
        try {
            const collectionRef = collection(DataOps.db, collectionName);
            const querySnapshot = await getDocs(collectionRef);

            const items = [];
            querySnapshot.forEach((doc) => {
                items.push({ id: doc.id, ...doc.data() });
            });

            return items;
        } catch (error) {
            console.error('Error fetching documents from Firebase:', error);
            return [];
        }
    }

    /**
     * Fetch upcoming sessions from Firebase.
     * Supports both newer sessions (officialStartDate/startAt) and legacy sessions (startDate/comparableDate).
     */
    static async fetchUpcomingSessionsFromFirebase() {
        try {
            const collectionRef = collection(DataOps.db, 'session');
            const querySnapshot = await getDocs(query(collectionRef));

            // Compare by day so "today" sessions are included.
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const upcomingSessions = querySnapshot.docs
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as any))
                .filter((sessionData) => {
                    const isDeleted = sessionData.deleted === true || sessionData.isHidden === true;
                    if (isDeleted) return false;

                    const sessionStartDate = TimeOps.getSessionStartDate(sessionData);
                    if (!sessionStartDate || isNaN(sessionStartDate.getTime())) return false;

                    const normalizedStart = new Date(sessionStartDate);
                    normalizedStart.setHours(0, 0, 0, 0);
                    return normalizedStart >= today;
                })
                .sort((a, b) => {
                    const aStart = TimeOps.getSessionStartDate(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
                    const bStart = TimeOps.getSessionStartDate(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
                    return aStart - bStart;
                });

            return upcomingSessions;

        } catch (error) {
            console.error('Error fetching upcoming sessions from Firebase:', error);
            return [];
        }
    }

    static async fetchItemsFromFirebase(collectionName: string, searchKey: string, searchValue: any) {
        try {
            const collectionRef = collection(DataOps.db, collectionName);
            const q = query(collectionRef, where(searchKey, "==", searchValue));
            const querySnapshot = await getDocs(q);

            const items = [];
            querySnapshot.forEach((doc) => {
                items.push({ id: doc.id, ...doc.data() });
            });

            return items;
        } catch (error) {
            console.error('Error fetching items from Firebase:', error);
            return [];
        }
    }

    static async fetchDataFromFirebase(collectionName: string, documentId: string, suppressErrors: boolean = false) {
        try {
            const cloudResult = await this.fetchCloudData(
                'fetchData',
                { collection: collectionName, id: documentId },
                {
                    retries: 2,
                    timeoutMs: 1500,
                    suppressErrors: true,
                    extractor: (json) => {
                        const { id, ...rest } = json || {};
                        return id ? rest : null;
                    }
                }
            );
            if (cloudResult) return cloudResult;

            const docRef = doc(DataOps.db, collectionName, documentId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists) {
                return docSnap.data();
            } else {
                return null;
            }
        } catch (error) {
            if (!suppressErrors) {
                console.error('Error fetching data from Firebase:', error);
            }
            return null;
        }
    }

    static async saveCustomDataToFirebase(collectionName: string, documentId: string, document: object, forceWrite: boolean) {
        try {
            console.log(`[DataOps] DEBUG: saveCustomDataToFirebase called for ${collectionName}/${documentId}`);
            if (!documentId) {
                console.log('[DataOps] DEBUG: No documentId, adding new document');
                const docRef = await addDoc(collection(DataOps.db, collectionName), document);
                console.log(`[DataOps] DEBUG: Added new document with ID: ${docRef.id}`);
                return Boolean(docRef.id);
            }

            const docRef = doc(DataOps.db, collectionName, documentId);
            const docSnapshot = await getDoc(docRef);

            if (docSnapshot.exists() && !forceWrite) {
                console.log('[DataOps] DEBUG: Document exists and forceWrite is false, skipping');
                return true;
            }

            console.log('[DataOps] DEBUG: Attempting upload via Cloud Function');
            const savedViaCloud = await this.uploadDocumentViaCF(collectionName, documentId, document, true);
            console.log(`[DataOps] DEBUG: Cloud Function upload result: ${savedViaCloud}`);
            if (savedViaCloud) return true;

            console.log('[DataOps] DEBUG: Falling back to direct setDoc (merge: true)');
            await setDoc(doc(DataOps.db, collectionName, documentId), document, { merge: true });
            console.log('[DataOps] DEBUG: direct setDoc completed');
            return true
        } catch (error) {
            console.error('[DataOps] ERROR in saveCustomDataToFirebase:', error);
            return false;
        }
    };

    static async saveDataToFirebase(collectionName: string, document: object) {
        try {
            if (document.hasOwnProperty('imageUrl')) {
                // to be implemented.
            } else {
                const docRef = await addDoc(collection(DataOps.db, collectionName), document);
                return docRef.id;
            }
        } catch (error) {
            console.error('Error saving user data:', error);
        }
    }

    static async retrieveDataFromStorage(dataName: string) {
        try {
            const data = await AsyncStorage.getItem(dataName);
            if (data !== null) {
                return JSON.parse(data);
            } else {
                return null;
            }
        } catch (error) {
            console.error('Error retrieving data:', error);
            return null;
        }
    }

    static async saveDataToStorage(dataName: string, data: any) {
        try {
            const dataString = JSON.stringify(data);
            await AsyncStorage.setItem(dataName, dataString);
        } catch (error) {
            console.error("Could not save data to storage.", error);
        }
    }

    static async retrieveCardsFromStorage() {
        try {
            const jsonCards = await AsyncStorage.getItem('cards');
            if (jsonCards !== null) {
                return JSON.parse(jsonCards);
            } else {
                return [];
            }
        } catch (error) {
            console.error('Error retrieving cards:', error);
            return [];
        }
    }

    static async saveCardsToStorage(cards) {
        try {
            const jsonCards = JSON.stringify(cards);
            await AsyncStorage.setItem('cards', jsonCards);

            for (const card of cards) {
                await AsyncStorage.setItem(card.campId.toString(), JSON.stringify(card));
            }
        } catch (error) {
            console.error("Could not save the items.", error);
        }
    }

    static async uploadImage(url, imageId) {
        try {
            const response = await fetch(url);
            const blob = await response.blob();

            const storageRef = ref(DataOps.storage, `images/` + imageId);

            const uploadResponse = await uploadBytes(storageRef, blob);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error('Error uploading image:', error);
        }
    }

    /** Upload a File object (web file input) to Firebase Storage and return the download URL. */
    static async uploadImageFile(file: File, imageId: string): Promise<string | null> {
        try {
            const storageRef = ref(DataOps.storage, `images/${imageId}`);
            await uploadBytes(storageRef, file);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error('Error uploading image file:', error);
            return null;
        }
    }

    /** Upload a generic File (e.g. PDF) to Firebase Storage under waivers/ and return the download URL. */
    static async uploadFile(file: File, path: string): Promise<string | null> {
        try {
            const storageRef = ref(DataOps.storage, path);
            await uploadBytes(storageRef, file);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error('Error uploading file:', error);
            return null;
        }
    }

    /** Fetch all non-deleted children for a parent from the `child` collection. */
    static async fetchChildren(parentId: string): Promise<any[]> {
        try {
            const colRef = collection(DataOps.db, 'child');
            const q = query(colRef, where('parentId', '==', parentId), where('deleted', '==', false));
            const snap = await getDocs(q);
            const results: any[] = [];
            snap.forEach((docSnap) => results.push({ userId: docSnap.id, ...docSnap.data() }));
            return results;
        } catch (e) {
            console.error('Error fetching children:', e);
            return [];
        }
    }

    /** Save or update a child document in the `child` collection. */
    static async saveChild(child: any): Promise<void> {
        const docRef = doc(DataOps.db, 'child', child.userId);
        await setDoc(docRef, { ...child, updatedAt: new Date().toISOString() }, { merge: true });
    }

    /** Soft-delete a child document in the `child` collection. */
    static async deleteChild(childId: string): Promise<void> {
        const docRef = doc(DataOps.db, 'child', childId);
        await setDoc(docRef, { deleted: true, deletedAt: new Date().toISOString() }, { merge: true });
    }

    // ============ SUBCOLLECTION METHODS FOR SCALABLE NOTIFICATIONS ============

    /**
     * Save document to a subcollection
     * Path: {parentCollection}/{parentId}/{subcollection}/{docId}
     * Example: users/user123/notifications/notif_001
     */
    static async saveToSubcollection(
        parentCollection: string,
        parentId: string,
        subcollection: string,
        docId: string,
        data: any
    ) {
        try {
            const docRef = doc(DataOps.db, parentCollection, parentId, subcollection, docId);
            await setDoc(docRef, data, { merge: true });
            return true;
        } catch (error) {
            console.error('Error saving to subcollection:', error);
            throw error;
        }
    }

    /**
     * Listen to subcollection changes with real-time updates
     * Returns unsubscribe function
     */
    static listenToSubcollection(
        parentCollection: string,
        parentId: string,
        subcollection: string,
        options: {
            orderBy?: { field: string; direction: 'asc' | 'desc' };
            limit?: number;
            where?: Array<{ field: string; operator: any; value: any }>;
        },
        onUpdate: (data: any[]) => void
    ) {
        try {
            const collectionRef = collection(DataOps.db, parentCollection, parentId, subcollection);

            let q = query(collectionRef);

            // Apply where clauses
            if (options.where) {
                options.where.forEach(condition => {
                    q = query(q, where(condition.field, condition.operator, condition.value));
                });
            }

            // Apply orderBy
            if (options.orderBy) {
                q = query(q, orderBy(options.orderBy.field, options.orderBy.direction));
            }

            // Apply limit
            if (options.limit) {
                q = query(q, limit(options.limit));
            }

            // Return unsubscribe function
            return onSnapshot(q, (snapshot) => {
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                onUpdate(data);
            }, (error) => {
                console.error('Error in subcollection listener:', error);
            });
        } catch (error) {
            console.error('Error setting up subcollection listener:', error);
            return () => { }; // Return empty unsubscribe function
        }
    }

    /**
     * Fetch documents from a subcollection
     */
    static async fetchFromSubcollection(
        parentCollection: string,
        parentId: string,
        subcollection: string,
        options?: {
            orderBy?: { field: string; direction: 'asc' | 'desc' };
            limit?: number;
            where?: Array<{ field: string; operator: any; value: any }>;
        }
    ) {
        try {
            const collectionRef = collection(DataOps.db, parentCollection, parentId, subcollection);

            let q = query(collectionRef);

            if (options?.where) {
                options.where.forEach(condition => {
                    q = query(q, where(condition.field, condition.operator, condition.value));
                });
            }

            if (options?.orderBy) {
                q = query(q, orderBy(options.orderBy.field, options.orderBy.direction));
            }

            if (options?.limit) {
                q = query(q, limit(options.limit));
            }

            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error('Error fetching from subcollection:', error);
            return [];
        }
    }

    /**
     * Compute great-circle distance between two points (miles)
     */
    private static haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const toRad = (v: number) => (v * Math.PI) / 180;
        const R = 3958.8; // miles
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    /**
     * Query camps within radius using geohash ranges stored at geolocation.geohash.
     * Requires compound index on geolocation.geohash ascending.
     */
    static async getCampsWithinRadius(center: { lat: number; lng: number }, radiusMiles: number): Promise<any[]> {
        try {
            if (!center || typeof center.lat !== 'number' || typeof center.lng !== 'number') return [];
            const bounds = GeoUtils.getGeohashQueryBounds(center.lat, center.lng, radiusMiles);
            const colRef = collection(DataOps.db, 'camp');
            const queries = bounds.map(([start, end]) => {
                return getDocs(
                    query(
                        colRef,
                        where('geolocation.geohash', '>=', start),
                        where('geolocation.geohash', '<=', end),
                        orderBy('geolocation.geohash')
                    )
                );
            });
            const snapshots = await Promise.all(queries);
            const camps: any[] = [];
            snapshots.forEach(snap => {
                snap.forEach(docSnap => {
                    const data: any = docSnap.data();
                    const geo = data?.geolocation;
                    if (geo?.lat != null && geo?.lng != null) {
                        const distance = DataOps.haversineMiles(center.lat, center.lng, geo.lat, geo.lng);
                        if (distance <= radiusMiles) {
                            camps.push({ campId: docSnap.id, ...data, distance });
                        }
                    }
                });
            });
            // Deduplicate by campId in case of overlapping bounds
            const seen = new Set();
            const deduped: any[] = [];
            for (const c of camps) {
                if (!seen.has(c.campId)) {
                    seen.add(c.campId);
                    deduped.push(c);
                }
            }
            return deduped;
        } catch (error) {
            console.error('Error querying camps within radius (geohash):', error);
            return [];
        }
    }

    /**
     * Update a document in a subcollection
     */
    static async updateSubcollectionDocument(
        parentCollection: string,
        parentId: string,
        subcollection: string,
        docId: string,
        data: any
    ) {
        try {
            const docRef = doc(DataOps.db, parentCollection, parentId, subcollection, docId);
            await updateDoc(docRef, data);
            return true;
        } catch (error) {
            console.error('Error updating subcollection document:', error);
            throw error;
        }
    }

    /**
     * Delete a document from a subcollection
     */
    static async deleteSubcollectionDocument(
        parentCollection: string,
        parentId: string,
        subcollection: string,
        docId: string
    ) {
        try {
            const docRef = doc(DataOps.db, parentCollection, parentId, subcollection, docId);
            await deleteDoc(docRef);
            return true;
        } catch (error) {
            console.error('Error deleting subcollection document:', error);
            throw error;
        }
    }

    /**
     * Get the collection name based on account type
     * user collection for family, host collection for hosts, coach collection for coaches
     */
    static getCollectionNameForAccountType(accountType: 'family' | 'user' | 'host' | 'coach'): string {
        switch (accountType) {
            case 'family':
            case 'user':
                return 'user';
            case 'host':
                return 'host';
            case 'coach':
                return 'coach';
            default:
                return 'user';
        }
    }

    /**
     * Fetch user from any collection (user, host, or coach) by userId
     * Returns { user, collection } where collection is the name of the collection where user was found
     * If user exists in multiple collections, prioritizes the one matching their accountType
     */
    static async fetchUserFromAnyCollection(userId: string): Promise<{ user: any; collection: string } | null> {
        const startTime = Date.now();
        try {
            const collections: Array<'user' | 'host' | 'coach'> = ['user', 'host', 'coach'];
            const directLookupResults = await Promise.all(
                collections.map((collectionName) =>
                    this.fetchCloudData(
                        'fetchData',
                        { collection: collectionName, id: userId },
                        { suppressErrors: true, timeoutMs: 2000 } // Short timeout for existence check
                    )
                )
            );

            for (let i = 0; i < directLookupResults.length; i++) {
                if (directLookupResults[i]) {
                    console.log(`[DataOps] User found via direct ID lookup in ${collections[i]} in ${Date.now() - startTime}ms`);
                    return { user: directLookupResults[i], collection: collections[i] };
                }
            }

            const result = await this.findUserByAuthIdAcrossCollections(userId);
            console.log(`[DataOps] User lookup finished in ${Date.now() - startTime}ms (found: ${!!result})`);
            return result;
        } catch (error) {
            console.error('Error fetching user from any collection:', error);
            return null;
        }
    }

    static async fetchUserByEmailFromAnyCollection(email: string): Promise<{ user: any; collection: string } | null> {
        try {
            const normalizedEmail = (email || '').trim().toLowerCase();
            if (!normalizedEmail) return null;

            const collections: Array<'user' | 'host' | 'coach'> = ['user', 'host', 'coach'];

            // Fast path: exact match query (works if emails are stored normalized).
            for (const collectionName of collections) {
                const exactMatches = await this.fetchItemsFromFirebase(collectionName, 'email', normalizedEmail);
                if (exactMatches.length > 0) {
                    const user = exactMatches[0];
                    return { user: { ...user, userId: user.userId || user.id }, collection: collectionName };
                }
            }

            // Fallback: case-insensitive scan for legacy mixed-case records.
            for (const collectionName of collections) {
                const allUsers = await this.fetchAllDocumentsFromFirebase(collectionName);
                const match = allUsers.find((user: any) => {
                    const candidateEmail = (user?.email || '').toString().trim().toLowerCase();
                    return candidateEmail === normalizedEmail;
                });
                if (match) {
                    return { user: { ...match, userId: match.userId || match.id }, collection: collectionName };
                }
            }

            return null;
        } catch (error) {
            console.error('[fetchUserByEmailFromAnyCollection] Error:', error);
            return null;
        }
    }

    static async findUserByAuthIdAcrossCollections(authId: string): Promise<{ user: any; collection: string } | null> {
        try {
            const result = await this.fetchCloudData(
                'findUserByAuthId',
                { authId },
                { suppressErrors: true, timeoutMs: 2500 }
            );

            if (result && typeof result === 'object' && 'collection' in (result as any) && 'data' in (result as any)) {
                return {
                    user: (result as any).data,
                    collection: (result as any).collection,
                };
            }

            // Fallback to parallel Firebase queries if Cloud Function fails or is slow
            const collections: Array<'user' | 'host' | 'coach'> = ['user', 'host', 'coach'];
            const searchPromises = collections.map(async (collectionName) => ({
                collection: collectionName,
                matches: await this.fetchItemsFromFirebase(collectionName, 'authId', authId)
            }));

            const allResults = await Promise.all(searchPromises);
            for (const res of allResults) {
                if (res.matches.length > 0) {
                    return { user: res.matches[0], collection: res.collection };
                }
            }

            return null;
        } catch (error) {
            console.error('[findUserByAuthIdAcrossCollections] Error searching by authId:', error);
            return null;
        }
    }

    /**
     * Save user to the appropriate collection based on account type
     */
    static async saveUserByAccountType(
        userId: string,
        userData: any,
        accountType: 'family' | 'user' | 'host' | 'coach',
        forceWrite: boolean = false
    ): Promise<boolean> {
        try {
            const collectionName = this.getCollectionNameForAccountType(accountType);
            await this.saveCustomDataToFirebase(collectionName, userId, userData, forceWrite);
            return true;
        } catch (error) {
            console.error('Error saving user by account type:', error);
            return false;
        }
    }

    /**
     * Check if user exists in the old 'user' collection with non-family account type
     * This is used for migration purposes
     */
    static async findUsersToMigrate(): Promise<Array<{ userId: string; user: any }>> {
        try {
            const allUsers = await this.fetchAllDocumentsFromFirebase('user');
            const usersToMigrate = allUsers.filter(
                (user: any) => user.accountType === 'host' || user.accountType === 'coach'
            );

            return usersToMigrate.map((user: any) => ({
                userId: user.userId || user.id,
                user: user
            }));
        } catch (error) {
            console.error('Error finding users to migrate:', error);
            return [];
        }
    }

    /**
     * Search for coaches by email or phone (partial match)
     * Returns coaches that match the search query
     */
    static async searchCoachesByEmail(searchQuery: string): Promise<any[]> {
        try {
            if (!searchQuery || searchQuery.trim().length < 2) {
                return [];
            }

            const allCoaches = await this.fetchAllDocumentsFromFirebase('coach');
            const query = searchQuery.toLowerCase().trim();

            // Filter coaches by email or phone match
            const matchingCoaches = allCoaches.filter((coach: any) => {
                const email = (coach.email || '').toLowerCase();
                const phone = (coach.phone || '').toLowerCase().replace(/\D/g, ''); // Remove non-digits
                const searchPhone = query.replace(/\D/g, ''); // Remove non-digits from search

                return email.includes(query) || phone.includes(searchPhone);
            });

            return matchingCoaches;
        } catch (error) {
            console.error('Error searching coaches:', error);
            return [];
        }
    }

    /**
     * Check if a string is a valid email format
     */
    static isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Check if a string is a valid phone format (at least 10 digits)
     */
    static isValidPhone(phone: string): boolean {
        const digits = phone.replace(/\D/g, '');
        return digits.length >= 10;
    }

    /**
     * Send coach invitation via email or SMS
     */
    static async sendCoachInvitation(contact: string, hostName: string): Promise<boolean> {
        try {
            // In a real app, you would:
            // 1. If email: Send invitation email via your email service (SendGrid, etc.)
            // 2. If phone: Send SMS via Twilio or similar service
            // For now, we'll store it in Firebase

            const isEmail = this.isValidEmail(contact);
            const isPhone = this.isValidPhone(contact);

            if (!isEmail && !isPhone) {
                throw new Error('Invalid email or phone number');
            }

            // Store pending invitation in Firebase
            await this.saveCustomDataToFirebase('pendingInvitations', undefined, {
                contact,
                hostName,
                invitedAt: new Date().toISOString(),
                type: isEmail ? 'email' : 'phone',
            }, false);

            return true;
        } catch (error) {
            console.error('Error sending coach invitation:', error);
            return false;
        }
    }

    /**
     * Get host as coach (hosts are automatically coaches too)
     * Converts host data to coach-compatible format
     */
    static async getHostAsCoach(hostId: string): Promise<any | null> {
        try {
            const hostData = await this.fetchDataFromFirebase('host', hostId);
            if (!hostData) return null;

            // Return host data formatted as a coach
            return {
                ...hostData,
                isHost: true, // Flag to indicate this is actually a host
                specialties: hostData.specialties || [],
                bio: hostData.bio || ''
            };
        } catch (error) {
            console.error('Error fetching host as coach:', error);
            return null;
        }
    }

    /**
     * Fetch coach objects for a list of coach IDs.
     * Falls back to host collection when a coach record does not exist.
     * Hosts are sorted to the front of the list.
     */
    static async fetchCoachDataList(coachIds: string[]): Promise<any[]> {
        try {
            if (!coachIds || coachIds.length === 0) return [];

            const results = await Promise.all(coachIds.map(async (coachId: string) => {
                try {
                    let coachData = await this.fetchDataFromFirebase('coach', coachId);
                    if (!coachData) {
                        coachData = await this.getHostAsCoach(coachId);
                    }
                    return coachData;
                } catch (e) {
                    return null;
                }
            }));

            const valid = results.filter(Boolean) as any[];
            // Hosts first
            valid.sort((a, b) => ((a.accountType === 'host') === (b.accountType === 'host')) ? 0 : (a.accountType === 'host' ? -1 : 1));
            return valid;
        } catch (error) {
            console.error('Error fetching coach data list:', error);
            return [];
        }
    }

    /**
     * Link a coach to a camp
     * Updates both the camp's coaches list and the coach's assignedCamps list
     */
    static async linkCoachToCamp(campId: string, coachId: string): Promise<boolean> {
        try {
            // Get current camp data
            const campData = await this.fetchDataFromFirebase('camp', campId);
            if (!campData) throw new Error('Camp not found');

            // Add coach to camp's coaches list
            const campCoaches = campData.campCoaches || [];
            if (!campCoaches.includes(coachId)) {
                campCoaches.push(coachId);
                await this.saveCustomDataToFirebase('camp', campId, { ...campData, campCoaches }, true);
            }

            // Update coach's assignedCamps (check if it's in coach collection or host collection)
            const coachData = await this.fetchDataFromFirebase('coach', coachId);
            if (coachData) {
                const assignedCamps = coachData.assignedCamps || [];
                if (!assignedCamps.includes(campId)) {
                    assignedCamps.push(campId);
                    await this.saveCustomDataToFirebase('coach', coachId, { ...coachData, assignedCamps }, true);
                }
            }

            return true;
        } catch (error) {
            console.error('Error linking coach to camp:', error);
            return false;
        }
    }

    /**
     * Unlink a coach from a camp
     */
    static async unlinkCoachFromCamp(campId: string, coachId: string): Promise<boolean> {
        try {
            // Remove coach from camp's coaches list
            const campData = await this.fetchDataFromFirebase('camp', campId);
            if (campData) {
                const campCoaches = (campData.campCoaches || []).filter((id: string) => id !== coachId);
                await this.saveCustomDataToFirebase('camp', campId, { ...campData, campCoaches }, true);
            }

            // Remove camp from coach's assignedCamps
            const coachData = await this.fetchDataFromFirebase('coach', coachId);
            if (coachData) {
                const assignedCamps = (coachData.assignedCamps || []).filter((id: string) => id !== campId);
                await this.saveCustomDataToFirebase('coach', coachId, { ...coachData, assignedCamps }, true);
            }

            return true;
        } catch (error) {
            console.error('Error unlinking coach from camp:', error);
            return false;
        }
    }

    /**
     * Link a coach to a host (for account-level relationship)
     * Updates both host's coaches list and coach's hostIds list
     */
    static async linkCoachToHost(hostId: string, coachId: string): Promise<boolean> {
        try {
            // Get current host data
            const hostData = await this.fetchDataFromFirebase('host', hostId);
            if (!hostData) throw new Error('Host not found');

            // Add coach to host's coaches list
            const coaches = hostData.coaches || [];
            if (!coaches.includes(coachId)) {
                coaches.push(coachId);
                await this.saveCustomDataToFirebase('host', hostId, { ...hostData, coaches }, true);
            }

            // Update coach's hostIds (check if it's in coach collection or host collection)
            const coachData = await this.fetchDataFromFirebase('coach', coachId);
            if (coachData) {
                const hostIds = coachData.hostIds || [];
                if (!hostIds.includes(hostId)) {
                    hostIds.push(hostId);
                    await this.saveCustomDataToFirebase('coach', coachId, { ...coachData, hostIds }, true);
                }
            } else {
                // Check if it's a host acting as a coach
                const hostAsCoach = await this.fetchDataFromFirebase('host', coachId);
                if (hostAsCoach) {
                    const hostIds = hostAsCoach.linkedHostIds || [];
                    if (!hostIds.includes(hostId)) {
                        hostIds.push(hostId);
                        await this.saveCustomDataToFirebase('host', coachId, { ...hostAsCoach, linkedHostIds: hostIds }, true);
                    }
                }
            }

            return true;
        } catch (error) {
            console.error('Error linking coach to host:', error);
            return false;
        }
    }

    /**
     * Unlink a coach from a host (for account-level relationship)
     * Updates both host's coaches list and coach's hostIds list
     */
    static async unlinkCoachFromHost(hostId: string, coachId: string): Promise<boolean> {
        try {
            // Remove coach from host's coaches list
            const hostData = await this.fetchDataFromFirebase('host', hostId);
            if (hostData) {
                const coaches = (hostData.coaches || []).filter((id: string) => id !== coachId);
                await this.saveCustomDataToFirebase('host', hostId, { ...hostData, coaches }, true);
            }

            // Remove host from coach's hostIds
            const coachData = await this.fetchDataFromFirebase('coach', coachId);
            if (coachData) {
                const hostIds = (coachData.hostIds || []).filter((id: string) => id !== hostId);
                await this.saveCustomDataToFirebase('coach', coachId, { ...coachData, hostIds }, true);
            } else {
                // Check if it's a host acting as a coach
                const hostAsCoach = await this.fetchDataFromFirebase('host', coachId);
                if (hostAsCoach) {
                    const hostIds = (hostAsCoach.linkedHostIds || []).filter((id: string) => id !== hostId);
                    await this.saveCustomDataToFirebase('host', coachId, { ...hostAsCoach, linkedHostIds: hostIds }, true);
                }
            }

            return true;
        } catch (error) {
            console.error('Error unlinking coach from host:', error);
            return false;
        }
    }

    /**
     * Check if email is already in use by another user
     */
    static async isEmailInUse(email: string, currentUserId?: string): Promise<boolean> {
        try {
            if (!email || email.trim() === '') return false;
            const normalizedEmail = email.toLowerCase().trim();

            const collections = ['user', 'host', 'coach'];

            for (const colName of collections) {
                const colRef = collection(this.db, colName);
                const q = query(colRef, where('email', '==', normalizedEmail));
                const snap = await getDocs(q);

                const otherUser = snap.docs.find(doc => doc.id !== currentUserId);
                if (otherUser) return true;
            }

            return false;
        } catch (error) {
            console.error('Error checking email availability:', error);
            return false; // Error should not block save if we can't prove it's in use
        }
    }

    /**
     * Check if phone is already in use by another user
     */
    static async isPhoneInUse(phone: string, currentUserId?: string): Promise<boolean> {
        try {
            if (!phone || phone.trim() === '') return false;

            // Normalize phone number (remove spaces, dashes, etc.)
            const normalizedPhone = phone.replace(/\D/g, '');

            const collections = ['user', 'host', 'coach'];

            for (const colName of collections) {
                const colRef = collection(this.db, colName);
                // Note: Phone numbers might be stored in different formats, 
                // but checking for exact match on normalized phone is a good start.
                // If the DB stores them with formatting, we'd need multiple queries or a cloud function.
                const q = query(colRef, where('phone', '==', phone));
                const snap = await getDocs(q);

                const otherUser = snap.docs.find(doc => {
                    const docData = doc.data();
                    const docPhone = (docData.phone || '').replace(/\D/g, '');
                    return doc.id !== currentUserId && docPhone === normalizedPhone;
                });

                if (otherUser) return true;
            }

            return false;
        } catch (error) {
            console.error('Error checking phone availability:', error);
            return false;
        }
    }

    /**
     * Add a session assignment to a coach document and ensure the camp is listed in assignedCamps
     */
    static async addSessionAssignmentToCoach(coachId: string, campId: string, sessionId: string): Promise<void> {
        try {
            const coachData = await this.fetchDataFromFirebase('coach', coachId);
            if (!coachData) return;

            const assignedSessions: string[] = Array.isArray(coachData.assignedSessions) ? coachData.assignedSessions : [];
            const nextAssignedSessions = assignedSessions.includes(sessionId)
                ? assignedSessions
                : [...assignedSessions, sessionId];

            const assignedCamps: string[] = Array.isArray(coachData.assignedCamps) ? coachData.assignedCamps : [];
            const nextAssignedCamps = assignedCamps.includes(campId)
                ? assignedCamps
                : [...assignedCamps, campId];

            await this.saveCustomDataToFirebase('coach', coachId, {
                ...coachData,
                assignedSessions: nextAssignedSessions,
                assignedCamps: nextAssignedCamps,
            }, true);
        } catch (error) {
            console.error('Error adding session assignment to coach:', error);
        }
    }

    /**
     * Remove a session assignment from a coach document
     */
    static async removeSessionAssignmentFromCoach(coachId: string, _campId: string, sessionId: string): Promise<void> {
        try {
            const coachData = await this.fetchDataFromFirebase('coach', coachId);
            if (!coachData) return;

            const assignedSessions: string[] = Array.isArray(coachData.assignedSessions) ? coachData.assignedSessions : [];
            const nextAssignedSessions = assignedSessions.filter((id: string) => id !== sessionId);

            await this.saveCustomDataToFirebase('coach', coachId, {
                ...coachData,
                assignedSessions: nextAssignedSessions,
            }, true);
        } catch (error) {
            console.error('Error removing session assignment from coach:', error);
        }
    }

    /**
     * Fetch all sessions where the provided coachId is assigned
     */
    static async fetchSessionsByCoach(coachId: string): Promise<any[]> {
        try {
            const colRef = collection(DataOps.db, 'session');
            const qRef = query(colRef, where('coach.userId', '==', coachId));
            const snap = await getDocs(qRef);
            const results: any[] = [];
            snap.forEach((docSnap) => {
                results.push({ sessionId: docSnap.id, ...docSnap.data() });
            });
            return results;
        } catch (error) {
            console.error('Error fetching sessions by coach:', error);
            return [];
        }
    }

    static async fetchActiveRegistrationsForSession(sessionId: string): Promise<any[]> {
        try {
            if (!sessionId) return [];
            const result = await this.fetchCloudData<any>(
                'getActiveRegistrationsForSession',
                { sessionId },
                { extractor: (json) => json?.registrations ?? [], suppressErrors: true }
            );
            return Array.isArray(result) ? result : [];
        } catch {
            return [];
        }
    }

    static async fetchStudentActivity(studentId: string, sessionId: string): Promise<any | null> {
        const result = await this.fetchCloudData<any>(
            'getStudentActivity',
            { studentId, sessionId },
            { extractor: (json) => json?.activity ?? null, suppressErrors: true }
        );
        return (result as any) ?? null;
    }

    static async fetchStudentActivitiesForSession(sessionId: string, studentIds: string[]): Promise<any[]> {
        try {
            const ids = (studentIds || []).filter(Boolean);
            if (!sessionId || ids.length === 0) return [];
            const result = await this.fetchCloudData<any>(
                'getStudentActivitiesForSession',
                { sessionId, studentIds: ids.join(',') },
                { extractor: (json) => json?.activities ?? [], suppressErrors: true }
            );
            return Array.isArray(result) ? result : [];
        } catch {
            return [];
        }
    }

    static async fetchSessionActivity(sessionId: string): Promise<any | null> {
        const result = await this.fetchCloudData<any>(
            'getSessionActivity',
            { sessionId },
            { extractor: (json) => json?.activity ?? null, suppressErrors: true }
        );
        return (result as any) ?? null;
    }

    static async fetchDailyActivityForSessionDate(sessionId: string, dateStr: string): Promise<any | null> {
        try {
            if (!sessionId || !dateStr) return null;
            const dateMillis = this.getMillisFromDisplayDate(dateStr);
            if (dateMillis === null) return null;
            const result = await this.fetchCloudData<any>(
                'getDailyActivityForSessionDate',
                { sessionId, dateMillis: String(dateMillis) },
                { extractor: (json) => json?.activity ?? null, suppressErrors: true }
            );
            return (result as any) ?? null;
        } catch {
            return null;
        }
    }

    static async updateActivityBatch(
        sessionId: string,
        studentId: string,
        date: string,
        dailyActivityData: any,
        studentActivityData: any
    ): Promise<boolean> {
        try {
            const dateMillis = this.getMillisFromDisplayDate(date);
            if (!sessionId || !studentId || !dateMillis) return false;

            const response = await fetch(`${this.getCloudFunctionBase()}/updateActivityBatch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId,
                    studentId,
                    date: dateMillis,
                    dailyActivityData: this.serializeTimestampsForCloud(dailyActivityData),
                    studentActivityData: this.serializeTimestampsForCloud(studentActivityData),
                }),
            });
            if (!response.ok) return false;
            const json = await response.json().catch(() => ({}));
            return Boolean((json as any)?.ok ?? response.ok);
        } catch (e) {
            console.error('updateActivityBatch (client) error', e);
            return false;
        }
    }

    static async updateClassActivityBatch(
        sessionId: string,
        date: string,
        dailyActivityData: any,
        sessionActivityData: any
    ): Promise<boolean> {
        try {
            const dateMillis = this.getMillisFromDisplayDate(date);
            if (!sessionId || !dateMillis) return false;

            const response = await fetch(`${this.getCloudFunctionBase()}/updateClassActivityBatch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId,
                    date: dateMillis,
                    dailyActivityData: this.serializeTimestampsForCloud(dailyActivityData),
                    sessionActivityData: this.serializeTimestampsForCloud(sessionActivityData),
                }),
            });
            if (!response.ok) return false;
            const json = await response.json().catch(() => ({}));
            return Boolean((json as any)?.ok ?? response.ok);
        } catch (e) {
            console.error('updateClassActivityBatch (client) error', e);
            return false;
        }
    }

    /**
     * Fetch all sessions for a camp by campId
     */
    static async fetchSessionsForCamp(campId: string): Promise<{ [key: string]: any }> {
        try {
            const colRef = collection(DataOps.db, 'session');
            const qRef = query(colRef, where('campId', '==', campId));
            const snap = await getDocs(qRef);
            const sessions: { [key: string]: any } = {};
            snap.forEach((docSnap) => {
                sessions[docSnap.id] = { sessionId: docSnap.id, ...docSnap.data() };
            });
            return sessions;
        } catch (error) {
            console.error('Error fetching sessions for camp:', error);
            return {};
        }
    }

    /**
     * Fetch all upcoming sessions for a camp by campId. Note endAt > now fetches all ongoing and future sessions,
     * while startAt > now fetches all future sessions.
     */
    static async fetchUpcomingSessionsForCamp(campId: string): Promise<{ [key: string]: any }> {
        try {
            const colRef = collection(DataOps.db, 'session');
            const qRef = query(colRef, where('campId', '==', campId), where('endAt', '>', new Date()));
            const snap = await getDocs(qRef);
            const sessions: { [key: string]: any } = {};
            snap.forEach((docSnap) => {
                sessions[docSnap.id] = { sessionId: docSnap.id, ...docSnap.data() };
            });
            return sessions;
        } catch (error) {
            console.error('Error fetching upcoming sessions for camp:', error);
            return {};
        }
    }

    /**
     * Efficiently count upcoming sessions for a camp using Firestore aggregate queries.
     * Attempts to filter on Timestamp fields first; falls back to client-side for string dates.
     */
    static async getUpcomingSessionCount(campId: string): Promise<number> {
        try {
            const colRef = collection(DataOps.db, 'session');
            const now = new Date();

            try {
                const queryResult = query(colRef, where('campId', '==', campId), where('endAt', '>', now));
                const snapshot = await getCountFromServer(queryResult);
                const count = (snapshot.data().count as number) || 0;
                if (count > 0) return count;
            } catch { }

            return 0;
        } catch (e) {
            console.error('Error counting upcoming sessions:', e);
            return 0;
        }
    }

    /** Create a session transaction record */
    static async createSessionTransaction(record: any): Promise<void> {
        try {
            const id = record?.transactionId || `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
            await DataOps.saveCustomDataToFirebase('session_transaction', id, { ...record, transactionId: id }, true);
        } catch (e) {
            console.error('Error creating session transaction:', e);
        }
    }

    /** Fetch recent session transactions for a specific parent user */
    static async fetchSessionTransactionsByUser(userId: string, maxItems: number = 50): Promise<any[]> {
        try {
            const colRef = collection(DataOps.db, 'session_transaction');
            const max = Math.max(1, Math.min(maxItems || 50, 200));
            let snap;
            try {
                const qRef = query(colRef, where('userId', '==', userId), orderBy('createdAt', 'desc'), limit(max));
                snap = await getDocs(qRef);
            } catch {
                // Fallback for environments without the composite index.
                const qRef = query(colRef, where('userId', '==', userId));
                snap = await getDocs(qRef);
            }

            const results: any[] = [];
            snap.forEach((docSnap) => {
                results.push({ transactionId: docSnap.id, ...docSnap.data() });
            });

            const toMillis = (value: any): number => {
                if (!value) return 0;
                if (value instanceof Timestamp) return value.toMillis();
                if (typeof value?.toDate === 'function') return value.toDate().getTime();
                if (typeof value?.seconds === 'number') return (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1e6);
                const parsed = new Date(value).getTime();
                return Number.isFinite(parsed) ? parsed : 0;
            };

            results.sort((a, b) => {
                const am = toMillis(a.createdAt || a.updatedAt);
                const bm = toMillis(b.createdAt || b.updatedAt);
                return bm - am;
            });

            return results.slice(0, max);
        } catch (e) {
            console.error('Error fetching session transactions by user:', e);
            return [];
        }
    }

    /** Find latest active registration for (sessionId, userId, childId) */
    static async findLatestRegistration(sessionId: string, userId: string, childId: string): Promise<any | null> {
        try {
            const colRef = collection(DataOps.db, 'registration');
            const qRef = query(colRef, where('sessionId', '==', sessionId), where('userId', '==', userId), where('childId', '==', childId));
            const snap = await getDocs(qRef);
            let latest: any = null;
            snap.forEach((docSnap) => {
                const data = { registrationId: docSnap.id, ...docSnap.data() } as any;
                if (!latest) latest = data;
                else {
                    const a = (latest.updatedAt || latest.createdAt || 0);
                    const b = (data.updatedAt || data.createdAt || 0);
                    if (new Date(b).getTime() > new Date(a).getTime()) latest = data;
                }
            });
            return latest;
        } catch (e) {
            return null;
        }
    }

    /** Fetch active (status === 'confirmed') registrations for a session */
    static async fetchActiveRegistrations(sessionId: string): Promise<any[]> {
        try {
            const colRef = collection(DataOps.db, 'registration');
            const qRef = query(colRef, where('sessionId', '==', sessionId), where('status', '==', 'confirmed'));
            const snap = await getDocs(qRef);
            const results: any[] = [];
            snap.forEach((docSnap) => results.push({ registrationId: docSnap.id, ...docSnap.data() }));
            return results;
        } catch (e) {
            return [];
        }
    }

    /** Fetch active registrations for a user (parent) */
    static async fetchActiveRegistrationsByUser(userId: string): Promise<any[]> {
        try {
            const colRef = collection(DataOps.db, 'registration');
            const qRef = query(colRef, where('userId', '==', userId), where('status', '==', 'confirmed'));
            const snap = await getDocs(qRef);
            const results: any[] = [];
            snap.forEach((docSnap) => results.push({ registrationId: docSnap.id, ...docSnap.data() }));
            return results;
        } catch (e) {
            return [];
        }
    }

}

export default DataOps;